import { Link } from "wouter";
import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await fetch("/.netlify/functions/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="bg-gray-50 border-t mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg font-semibold mb-4">Ariadne's Gallery</h3>
            <p className="text-gray-600 text-sm">
              Explore the emotional connections between artworks through an interactive knowledge graph.
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link href="/" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/about" className="text-gray-600 hover:text-gray-900 transition-colors">
                  About
                </Link>
              </li>
              <li>
                <a href="/artists/" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Artists
                </a>
              </li>
              <li>
                <a href="/styles/" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Art Styles
                </a>
              </li>
              <li>
                <a href="/museums/" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Museums
                </a>
              </li>
              <li>
                <Link href="/emotion" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Emotion Collections
                </Link>
              </li>
              <li>
                <Link href="/emotion-search" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Emotion Search
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium mb-4">Features</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>Emotion-based artwork connections</li>
              <li>Interactive knowledge graph</li>
              <li>Comprehensive art database</li>
              <li>Search & discovery tools</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-4">Weekly Art Digest</h4>
            <p className="text-sm text-gray-600 mb-3">
              Curated artworks and emotional connections, delivered weekly.
            </p>
            {status === "success" ? (
              <p className="text-sm text-emerald-600 font-medium">You're subscribed!</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors disabled:opacity-60"
                >
                  {status === "loading" ? "Subscribing..." : "Subscribe"}
                </button>
                {status === "error" && (
                  <p className="text-xs text-red-500">Something went wrong. Try again.</p>
                )}
              </form>
            )}
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © 2025 Ariadne's Gallery. Art data sourced from public collections.
            </p>
            <p className="text-sm text-gray-500 mt-2 md:mt-0">
              Built with emotion analysis and graph theory
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}